import clsx from 'clsx'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect } from 'react'
import type { LandingpageMappingRow } from '../../lib/referer/matchLandingpageMapping'
import { buildParentsBranchPickerLinks } from '../../utils/parentsLandingBooking'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'
import TextHeadline from '../ui/texts/TextHeadline'
import ButtonIcon from '../ui/buttons/ButtonIcon'
import TextButtonFlat from '../ui/buttons/TextButtonFlat'
import IconClose from '../ui/icons/IconClose'

type AppointmentSelectionModalProps = {
    open: boolean
    onClose: () => void
    mappingRows: LandingpageMappingRow[]
    className?: string
}

const cardVariants = {
    hidden: { scale: 0.85, opacity: 0, y: 20 },
    visible: {
        scale: 1,
        opacity: 1,
        y: 0,
        transition: {
            type: 'spring' as const,
            stiffness: 380,
            damping: 22,
            mass: 0.8,
        },
    },
    exit: {
        scale: 0.9,
        opacity: 0,
        transition: { duration: 0.2 },
    },
}

export default function AppointmentSelectionModal({
    open,
    onClose,
    mappingRows,
    className,
}: AppointmentSelectionModalProps) {
    const isStele = useSteleBreakpoint()

    useEffect(() => {
        if (!open) {
            return
        }
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose()
            }
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [open, onClose])

    const links = buildParentsBranchPickerLinks(mappingRows)

    return (
        <AnimatePresence>
            {open && (
                <div
                    className={clsx(
                        'pointer-events-auto fixed inset-0 z-[95] flex items-center justify-center bg-black/55 px-4 py-6 backdrop-blur-sm',
                        className,
                    )}
                >
                    <button
                        type='button'
                        className='absolute inset-0 cursor-default'
                        aria-label='Schließen'
                        onClick={onClose}
                    />
                    <motion.div
                        key='appointment-selection-card'
                        className={clsx(
                            'relative z-10 flex w-full max-w-100 flex-col items-center stele:max-w-400',
                        )}
                        variants={cardVariants}
                        initial='hidden'
                        animate='visible'
                        exit='exit'
                        role='dialog'
                        aria-modal='true'
                        aria-labelledby='appointment-selection-title'
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div
                            className={clsx(
                                'relative flex w-full flex-col items-center drop-shadow-2-black-stroke bg-white-stroke-even-darker border-black-stroke pattern-background',
                                'gap-2 border px-6 pt-2 pb-8 rounded-4xl',
                                'stele:gap-8 stele:border-4 stele:px-24 stele:pt-16 stele:pb-32 stele:rounded-[128px]',
                            )}
                        >
                            <TextHeadline
                                colorPreset='red'
                                fontSize={isStele ? 96 : 24}
                                maxCharsPerLine={isStele ? 22 : 18}
                                leading={0.95}
                                tracking='-2%'
                            >
                                WO Möchtest du Die Karte abholen?
                            </TextHeadline>
                            <p
                                className={clsx(
                                    'text-white-stroke-lightest font-nunito font-bold text-center tracking-[-2%]',
                                    'text-[16px] leading-[22px]',
                                    'stele:text-[64px] stele:leading-22',
                                )}
                                style={{ textShadow: '0 1px 0 #06487C' }}
                            >
                                Wähle deine teilnehmende Sparkasse aus, um einen Termin zu vereinbaren.
                            </p>
                            <ul
                                className={clsx(
                                    'flex min-h-0 w-full touch-pan-y flex-col overflow-y-auto overscroll-y-contain',
                                    'max-h-[min(50vh,20rem)] gap-2 pt-2 pb-2',
                                    'stele:max-h-[min(50vh,48rem)] stele:gap-8 stele:pt-10',
                                )}
                            >
                                {links.length === 0 ? (
                                    <li
                                        className={clsx(
                                            'text-white-stroke-lightest font-nunito font-bold text-center',
                                            'text-[16px]',
                                            'stele:text-[48px]',
                                        )}
                                    >
                                        Keine Filial-Links verfügbar.
                                    </li>
                                ) : (
                                    links.map(({ documentId, name, url }) => (
                                        <li key={documentId}>
                                            <TextButtonFlat
                                                href={url}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                            >
                                                {name}
                                            </TextButtonFlat>
                                        </li>
                                    ))

                                )}
                            </ul>
                            <div
                                className={clsx(
                                    'absolute',
                                    '-bottom-6',
                                    'stele:-bottom-24',
                                )}
                            >
                            <ButtonIcon
                                icon={<IconClose width='100%' height='100%' />}
                                variant='standalone'
                                color='red'
                                popupCloseSound
                                onClick={onClose}
                                aria-label='Schließen'
                            />
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    )
}

